import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCollection, setFilterCollection] = useState("all");

  const fetchProducts = async () => {
    try {
      const res = await fetch("/api/products");
      const data = await res.json();
      setProducts(data.products || []);
    } catch (err) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      await fetch(`/api/products/${id}`, {
        method: "DELETE",
      });

      setProducts((prev) => prev.filter((p) => p._id !== id));
      toast.success("Product deleted successfully");
    } catch {
      toast.error("Failed to delete product");
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Get unique collections
  const collections = ["all", ...new Set(products.map((p) => p.collectionName).filter(Boolean))];

  // Filter products based on search and collection
  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.model?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCollection = filterCollection === "all" || p.collectionName === filterCollection;
    return matchesSearch && matchesCollection;
  });

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Products</h1>
          <p style={styles.subtitle}>Manage your product catalog</p>
        </div>
        <Link to="/admin/products/new" style={styles.addBtn}>
          <span style={styles.btnIcon}>+</span> Add Product
        </Link>
      </div>

      {/* Search and Filter Section */}
      <div style={styles.filterSection}>
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <select
          value={filterCollection}
          onChange={(e) => setFilterCollection(e.target.value)}
          style={styles.filterSelect}
        >
          {collections.map((collection) => (
            <option key={collection} value={collection}>
              {collection === "all" ? "All Collections" : collection}
            </option>
          ))}
        </select>

        <div style={styles.statsChip}>
          {filteredProducts.length} {filteredProducts.length === 1 ? "product" : "products"}
        </div>
      </div>

      {/* Products Table */}
      {filteredProducts.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📦</div>
          <h3 style={styles.emptyTitle}>No products found</h3>
          <p style={styles.emptyText}>
            {searchTerm || filterCollection !== "all"
              ? "Try adjusting your search or filter"
              : "Get started by adding your first product"}
          </p>
          {!searchTerm && filterCollection === "all" && (
            <Link to="/admin/products/new" style={styles.emptyBtn}>
              Add Your First Product
            </Link>
          )}
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}>Product</th>
                <th style={styles.th}>Collection</th>
                <th style={styles.th}>Price</th>
                <th style={styles.th}>Variants</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredProducts.map((p) => (
                <tr key={p._id} style={styles.tableRow}>
                  <td style={styles.td}>
                    <div style={styles.productCell}>
                      <div style={styles.productImage}>
                        {p.variants?.[0]?.images?.[0] ? (
                          <img
                            src={p.variants[0].images[0]}
                            alt={p.model}
                            style={styles.thumbnail}
                          />
                        ) : (
                          <div style={styles.placeholderImage}>📷</div>
                        )}
                      </div>
                      <div style={styles.productInfo}>
                        <div style={styles.productName}>{p.model}</div>
                        <div style={styles.productId}>ID: {p._id.slice(-8)}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.collectionBadge}>{p.collectionName || "—"}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.price}>{p.price} DT</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.variantCount}>{p.variants?.length || 0}</span>
                  </td>
                  <td style={styles.td}>
                    {p.sale?.isOnSale ? (
                      <span style={styles.saleBadge}>🏷️ On Sale</span>
                    ) : (
                      <span style={styles.regularBadge}>Regular</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      <Link to={`/admin/products/edit/${p._id}`} style={styles.editBtn}>
                        ✏️ Edit
                      </Link>
                      <Link to={`/admin/products/${p._id}/variants`} style={styles.variantsBtn}>
                        🎨 Variants
                      </Link>
                      <button onClick={() => deleteProduct(p._id)} style={styles.deleteBtn}>
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: "32px",
    background: "#f8f9fa",
    minHeight: "100vh",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    gap: "16px",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid #e0e0e0",
    borderTop: "4px solid #111",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    fontSize: "16px",
    color: "#666",
    fontWeight: "500",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "32px",
  },
  title: {
    fontSize: "32px",
    fontWeight: "700",
    marginBottom: "4px",
    color: "#111",
  },
  subtitle: {
    fontSize: "15px",
    color: "#666",
    margin: 0,
  },
  addBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 24px",
    background: "linear-gradient(135deg, #111 0%, #333 100%)",
    color: "#fff",
    borderRadius: "8px",
    textDecoration: "none",
    fontWeight: "600",
    fontSize: "15px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    transition: "all 0.3s ease",
  },
  btnIcon: {
    fontSize: "20px",
    fontWeight: "300",
  },
  filterSection: {
    display: "flex",
    gap: "16px",
    marginBottom: "24px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  searchWrapper: {
    position: "relative",
    flex: "1",
    minWidth: "250px",
  },
  searchIcon: {
    position: "absolute",
    left: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "16px",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    padding: "12px 16px 12px 44px",
    fontSize: "15px",
    border: "2px solid #e0e0e0",
    borderRadius: "8px",
    outline: "none",
    transition: "all 0.3s ease",
    background: "#fff",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.04)",
  },
  filterSelect: {
    padding: "12px 16px",
    fontSize: "15px",
    border: "2px solid #e0e0e0",
    borderRadius: "8px",
    outline: "none",
    cursor: "pointer",
    background: "#fff",
    fontWeight: "500",
    color: "#333",
    minWidth: "180px",
    transition: "all 0.3s ease",
  },
  statsChip: {
    padding: "8px 16px",
    background: "#e3f2fd",
    color: "#1976d2",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: "600",
  },
  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
  },
  emptyIcon: {
    fontSize: "64px",
    marginBottom: "16px",
  },
  emptyTitle: {
    fontSize: "24px",
    fontWeight: "600",
    marginBottom: "8px",
    color: "#333",
  },
  emptyText: {
    fontSize: "16px",
    color: "#666",
    marginBottom: "24px",
  },
  emptyBtn: {
    display: "inline-block",
    padding: "12px 24px",
    background: "#111",
    color: "#fff",
    borderRadius: "8px",
    textDecoration: "none",
    fontWeight: "600",
    transition: "all 0.3s ease",
  },
  tableWrapper: {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
  },
  th: {
    padding: "16px 20px",
    textAlign: "left",
    fontSize: "13px",
    fontWeight: "700",
    color: "#555",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #e0e0e0",
  },
  tableRow: {
    transition: "all 0.2s ease",
    borderBottom: "1px solid #f0f0f0",
  },
  td: {
    padding: "16px 20px",
    fontSize: "15px",
    color: "#333",
  },
  productCell: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  productImage: {
    width: "48px",
    height: "48px",
    flexShrink: 0,
  },
  thumbnail: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "6px",
  },
  placeholderImage: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f0f0f0",
    borderRadius: "6px",
    fontSize: "20px",
  },
  productInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  productName: {
    fontWeight: "600",
    color: "#111",
  },
  productId: {
    fontSize: "12px",
    color: "#999",
    fontFamily: "monospace",
  },
  collectionBadge: {
    display: "inline-block",
    padding: "4px 12px",
    background: "#f0f0f0",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "500",
    color: "#555",
  },
  price: {
    fontWeight: "600",
    fontSize: "16px",
    color: "#111",
  },
  variantCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "28px",
    height: "28px",
    padding: "0 8px",
    background: "#e3f2fd",
    color: "#1976d2",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "700",
  },
  saleBadge: {
    display: "inline-block",
    padding: "6px 12px",
    background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)",
    color: "#fff",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "600",
  },
  regularBadge: {
    display: "inline-block",
    padding: "6px 12px",
    background: "#f0f0f0",
    color: "#666",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "500",
  },
  actions: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  editBtn: {
    padding: "8px 12px",
    background: "#e3f2fd",
    color: "#1976d2",
    borderRadius: "6px",
    textDecoration: "none",
    fontSize: "13px",
    fontWeight: "600",
    transition: "all 0.2s ease",
    border: "none",
    cursor: "pointer",
  },
  variantsBtn: {
    padding: "8px 12px",
    background: "#f3e5f5",
    color: "#7b1fa2",
    borderRadius: "6px",
    textDecoration: "none",
    fontSize: "13px",
    fontWeight: "600",
    transition: "all 0.2s ease",
    border: "none",
    cursor: "pointer",
  },
  deleteBtn: {
    padding: "8px 12px",
    background: "#ffebee",
    color: "#c62828",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "600",
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
};

// Add CSS for spinner animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  table tbody tr:hover {
    background: #f8f9fa !important;
    transform: scale(1.001);
  }
  
  a[style*="editBtn"]:hover,
  a[style*="variantsBtn"]:hover,
  button[style*="deleteBtn"]:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  input[style*="searchInput"]:focus {
    border-color: #1976d2 !important;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1) !important;
  }
  
  select[style*="filterSelect"]:focus {
    border-color: #1976d2 !important;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1) !important;
  }
  
  a[style*="addBtn"]:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
  }
  
  a[style*="emptyBtn"]:hover {
    background: #333 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;
document.head.appendChild(styleSheet);

export default AdminProducts;
